import { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  User,
  Settings,
  BookOpen,
  Award,
  Bell,
  LogOut,
  Calendar,
  Camera,
  Sparkles,
  Copy,
  Bot,
  Lock,
  Loader2,
} from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import { CertificatesTab } from '@/components/certificate/CertificatesTab'
import { Progress } from '@/components/ui/progress'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import ActivityGraph from '@/components/ActivityGraph'
import BadgesCard from '@/components/BadgesCard'
import { TwoFactorSettings } from '@/components/profile/TwoFactorSettings'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/hooks'
import { authService } from '@/services'
import { useUploadAvatar } from '@/services/upload.service'
import PasswordStrengthIndicator, { isPasswordStrong } from '@/components/PasswordStrengthIndicator'
import { getErrorMessage } from '@/utils/error'
import { isFCamaraEmail } from '@/utils/fcamara'

const UserProfile = () => {
  const { toast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user: authUser, isAuthenticated, isLoading, logout: authLogout } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [avatarUrl, setAvatarUrl] = useState<string>('')
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const activeTab: 'progress' | 'settings' | 'certificados' =
    tabParam === 'settings' ? 'settings' : tabParam === 'certificados' ? 'certificados' : 'progress'

  // Hook para upload de avatar
  const { mutate: uploadAvatar, isPending: isUploadingAvatar } = useUploadAvatar()

  // Mutation para atualizar perfil
  const updateProfileMutation = useMutation({
    mutationFn: authService.updateProfile,
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(['auth', 'me'], updatedUser)
      toast({
        title: 'Perfil atualizado!',
        description: 'Suas informações foram salvas com sucesso.',
      })
      setIsEditing(false)
      setEditForm({
        ...editForm,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      })
    },
    onError: (error: unknown) => {
      toast({
        title: 'Erro ao atualizar perfil',
        description: getErrorMessage(error, 'Tente novamente mais tarde.'),
        variant: 'destructive',
      })
    },
  })

  // Redireciona para login se não estiver autenticado
  useEffect(() => {
    // Só redireciona se NÃO estiver carregando E NÃO estiver autenticado
    if (!isLoading && !isAuthenticated) {
      navigate('/auth')
    }
  }, [isAuthenticated, isLoading, navigate])

  // Carrega avatar atual do usuário
  useEffect(() => {
    if (authUser?.avatar) {
      setAvatarUrl(authUser.avatar)
    }
  }, [authUser?.avatar])

  // Format joined date from createdAt
  const formatJoinedDate = (dateString?: string) => {
    if (!dateString) return 'Data desconhecida'

    const date = new Date(dateString)
    const months = [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro',
    ]

    return `${months[date.getMonth()]} ${date.getFullYear()}`
  }

  // User data from auth
  const user = {
    name: authUser?.name || 'Usuário',
    email: authUser?.email || 'usuario@exemplo.com',
    avatar: authUser?.avatar || '',
    joinedDate: formatJoinedDate(authUser?.createdAt),
  }

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    name: user.name,
    email: user.email,
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })

  // Check if email is FCamara (cannot be changed)
  const isFCamaraUserEmail = isFCamaraEmail(user.email)

  const handleSaveProfile = () => {
    // Validate passwords if trying to change
    if (editForm.newPassword) {
      if (!editForm.currentPassword) {
        toast({
          title: 'Erro',
          description: 'Digite sua senha atual para alterá-la.',
          variant: 'destructive',
        })
        return
      }
      if (editForm.newPassword !== editForm.confirmPassword) {
        toast({
          title: 'Erro',
          description: 'As senhas não coincidem.',
          variant: 'destructive',
        })
        return
      }
      if (!isPasswordStrong(editForm.newPassword)) {
        toast({
          title: 'Senha fraca',
          description: 'A senha deve atender todos os requisitos de segurança.',
          variant: 'destructive',
        })
        return
      }
    }

    // Validate email if changed and not FCamara
    if (editForm.email && editForm.email !== user.email && !isFCamaraUserEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(editForm.email)) {
        toast({
          title: 'Erro',
          description: 'Digite um e-mail válido.',
          variant: 'destructive',
        })
        return
      }
    }

    // Prepara dados para enviar (campos enviados condicionalmente)
    const updateData: {
      name?: string
      email?: string
      currentPassword?: string
      newPassword?: string
    } = {}

    if (editForm.name !== user.name) {
      updateData.name = editForm.name
    }

    if (editForm.email && editForm.email !== user.email && !isFCamaraUserEmail) {
      updateData.email = editForm.email
    }

    if (editForm.newPassword) {
      updateData.currentPassword = editForm.currentPassword
      updateData.newPassword = editForm.newPassword
    }

    // Chama mutation para atualizar
    updateProfileMutation.mutate(updateData)
  }

  const handleCancelEdit = () => {
    setEditForm({
      name: user.name,
      email: user.email,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    })
    setIsEditing(false)
  }

  const handleLogout = () => {
    authLogout()
    navigate('/')
  }

  // Mostra loading enquanto verifica autenticação
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Carregando perfil...</p>
        </div>
      </div>
    )
  }

  // Se não está carregando e não está autenticado, mostra mensagem de redirecionamento
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Redirecionando para login...</p>
        </div>
      </div>
    )
  }

  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Formato inválido',
          description: 'Por favor, selecione um arquivo de imagem.',
          variant: 'destructive',
        })
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Arquivo muito grande',
          description: 'A imagem deve ter no máximo 5MB.',
          variant: 'destructive',
        })
        return
      }

      // Create a local URL for preview (temporary)
      const localUrl = URL.createObjectURL(file)
      setAvatarUrl(localUrl)

      // Upload real do arquivo (passa a URL antiga para deletar)
      uploadAvatar(
        {
          file,
          oldAvatarUrl: authUser?.avatar || undefined,
        },
        {
          onSuccess: (uploadedUrl) => {
            // Atualiza com a URL real do servidor
            setAvatarUrl(uploadedUrl)
            toast({
              title: 'Foto atualizada!',
              description: 'Sua foto de perfil foi alterada com sucesso.',
            })
            // Libera a URL local
            URL.revokeObjectURL(localUrl)
          },
          onError: (error: unknown) => {
            // Reverte para o avatar original em caso de erro
            setAvatarUrl(authUser?.avatar || '')
            toast({
              title: 'Erro ao fazer upload',
              description: getErrorMessage(error, 'Tente novamente mais tarde.'),
              variant: 'destructive',
            })
            // Libera a URL local
            URL.revokeObjectURL(localUrl)
          },
        }
      )
    }
  }

  // Mock progress data - Temporariamente comentado (aguardando implementação do backend)
  /* const courseProgress = {
    totalModules: 6,
    completedModules: 2,
    totalEpisodes: 24,
    completedEpisodes: 8,
    totalExercises: 120,
    completedExercises: 10,
  };

  // Mock practical learning data
  const practicalLearning = {
    promptsCreated: 5,
    agentsCreated: 2,
    promptsCopied: 12,
  };

  const practicalScore =
    (practicalLearning.promptsCreated * 10) +
    (practicalLearning.agentsCreated * 20) +
    (practicalLearning.promptsCopied * 5);

  const overallProgress = Math.round((courseProgress.completedEpisodes / courseProgress.totalEpisodes) * 100); */

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent/5 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      <main className="container py-8 relative z-10">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar para o início
        </Link>

        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-8"
        >
          <div className="relative group">
            <Avatar className="w-24 h-24 border-4 border-primary/20">
              <AvatarImage src={avatarUrl || authUser?.avatar} alt={user.name} />
              <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                {user.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <button
              onClick={handleAvatarClick}
              disabled={isUploadingAvatar}
              className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer disabled:cursor-not-allowed"
              title={isUploadingAvatar ? 'Fazendo upload...' : 'Alterar foto de perfil'}
            >
              {isUploadingAvatar ? (
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              ) : (
                <Camera className="w-8 h-8 text-white" />
              )}
            </button>
            {isUploadingAvatar && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 rounded-full">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
              disabled={isUploadingAvatar}
            />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground">{user.name}</h1>
            <p className="text-muted-foreground">{user.email}</p>
            <p className="text-sm text-muted-foreground mt-1">Membro desde {user.joinedDate}</p>
          </div>
        </motion.div>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={(value) => setSearchParams({ tab: value })}
          className="w-full"
        >
          <TabsList className="grid w-full mb-8 grid-cols-3">
            <TabsTrigger value="progress" className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Meu Progresso
            </TabsTrigger>
            <TabsTrigger value="certificados" className="flex items-center gap-2">
              <Award className="w-4 h-4" />
              Certificados
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Configurações
            </TabsTrigger>
          </TabsList>

          {/* Progress Tab */}
          <TabsContent value="progress">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="grid gap-6"
            >
              {/* Overall Progress Card - Temporariamente oculto (aguardando implementação do backend) */}
              {/* <Card className="glass-card border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-primary" />
                    Progresso Geral
                  </CardTitle>
                  <CardDescription>
                    Acompanhe sua evolução com a IA
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Conclusão do curso</span>
                        <span className="text-sm font-medium text-primary">{overallProgress}%</span>
                      </div>
                      <Progress value={overallProgress} className="h-3" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-muted/30 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-primary">{courseProgress.completedEpisodes}</p>
                        <p className="text-sm text-muted-foreground">de {courseProgress.totalEpisodes} episódios</p>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-primary">{courseProgress.completedExercises}</p>
                        <p className="text-sm text-muted-foreground">de {courseProgress.totalExercises} exercícios</p>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-primary">{courseProgress.completedModules}</p>
                        <p className="text-sm text-muted-foreground">de {courseProgress.totalModules} módulos</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card> */}

              {/* Practical Learning Card - Temporariamente oculto (aguardando implementação do backend) */}
              {/* <Card className="glass-card border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Aprendizado Prático
                  </CardTitle>
                  <CardDescription>
                    Suas contribuições e interações no Sym! Hub, a comunidade de IA da FCamara
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-4 text-center">
                      <p className="text-sm text-muted-foreground mb-1">Pontuação Total</p>
                      <p className="text-4xl font-bold text-primary">{practicalScore}</p>
                      <p className="text-xs text-muted-foreground mt-1">pontos</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-muted/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-primary" />
                          <span className="text-sm text-muted-foreground">Prompts criados</span>
                        </div>
                        <p className="text-2xl font-bold text-foreground">{practicalLearning.promptsCreated}</p>
                        <p className="text-xs text-muted-foreground">+10 pts cada</p>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Bot className="w-4 h-4 text-primary" />
                          <span className="text-sm text-muted-foreground">Agentes criados</span>
                        </div>
                        <p className="text-2xl font-bold text-foreground">{practicalLearning.agentsCreated}</p>
                        <p className="text-xs text-muted-foreground">+20 pts cada</p>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Copy className="w-4 h-4 text-primary" />
                          <span className="text-sm text-muted-foreground">Prompts copiados</span>
                        </div>
                        <p className="text-2xl font-bold text-foreground">{practicalLearning.promptsCopied}</p>
                        <p className="text-xs text-muted-foreground">+5 pts cada</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card> */}

              {/* Badges Card */}
              <BadgesCard />

              {/* Activity Graph */}
              <Card className="glass-card border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    Histórico de Atividades
                  </CardTitle>
                  <CardDescription>Visualize sua consistência ao longo do ano</CardDescription>
                </CardHeader>
                <CardContent>
                  <ActivityGraph />
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Certificados Tab — liberada pra todos os usuários. */}
          <TabsContent value="certificados">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <CertificatesTab />
            </motion.div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="grid gap-6"
            >
              {/* Account Settings */}
              <Card className="glass-card border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" />
                    Dados da Conta
                  </CardTitle>
                  <CardDescription>Gerencie suas informações pessoais</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Nome */}
                  <div className="grid gap-2">
                    <Label htmlFor="name">Nome</Label>
                    {isEditing ? (
                      <Input
                        id="name"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        placeholder="Seu nome"
                      />
                    ) : (
                      <p className="text-foreground">{user.name}</p>
                    )}
                  </div>

                  <Separator />

                  {/* Email */}
                  <div className="grid gap-2">
                    <Label htmlFor="email">E-mail</Label>
                    {isEditing && !isFCamaraUserEmail ? (
                      <Input
                        id="email"
                        value={editForm.email || user.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        placeholder="seu@email.com"
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <Input
                          id="email"
                          value={user.email}
                          disabled
                          className={isFCamaraUserEmail ? 'bg-muted cursor-not-allowed' : ''}
                        />
                        {isFCamaraUserEmail && <Lock className="w-4 h-4 text-muted-foreground" />}
                      </div>
                    )}
                    {isFCamaraUserEmail && (
                      <p className="text-xs text-muted-foreground">
                        E-mails @fcamara.com e @fcamara.com.br não podem ser alterados
                      </p>
                    )}
                  </div>

                  {isEditing && (
                    <>
                      <Separator />

                      {/* Alterar Senha */}
                      <div className="space-y-4 pt-2">
                        <h4 className="font-medium text-sm">Alterar Senha</h4>

                        <div className="grid gap-2">
                          <Label htmlFor="currentPassword">Senha Atual</Label>
                          <Input
                            id="currentPassword"
                            type="password"
                            value={editForm.currentPassword}
                            onChange={(e) =>
                              setEditForm({ ...editForm, currentPassword: e.target.value })
                            }
                            placeholder="Digite sua senha atual"
                          />
                        </div>

                        <div className="grid gap-2">
                          <Label htmlFor="newPassword">Nova Senha</Label>
                          <Input
                            id="newPassword"
                            type="password"
                            value={editForm.newPassword}
                            onChange={(e) =>
                              setEditForm({ ...editForm, newPassword: e.target.value })
                            }
                            placeholder="Mín. 8 chars, maiúscula, minúscula, número, símbolo"
                          />
                          <PasswordStrengthIndicator
                            password={editForm.newPassword}
                            confirmPassword={editForm.confirmPassword}
                          />
                        </div>

                        <div className="grid gap-2">
                          <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
                          <Input
                            id="confirmPassword"
                            type="password"
                            value={editForm.confirmPassword}
                            onChange={(e) =>
                              setEditForm({ ...editForm, confirmPassword: e.target.value })
                            }
                            placeholder="Digite a nova senha novamente"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <Separator />

                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-4">
                    {isEditing ? (
                      <>
                        <Button
                          onClick={handleSaveProfile}
                          className="flex-1"
                          disabled={updateProfileMutation.isPending}
                        >
                          {updateProfileMutation.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Salvando...
                            </>
                          ) : (
                            'Salvar Alterações'
                          )}
                        </Button>
                        <Button
                          onClick={handleCancelEdit}
                          variant="outline"
                          className="flex-1"
                          disabled={updateProfileMutation.isPending}
                        >
                          Cancelar
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => setIsEditing(true)}
                        variant="outline"
                        className="w-full"
                      >
                        Editar Perfil
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Autenticação de dois fatores */}
              {authUser && <TwoFactorSettings user={authUser} />}

              {/* Notification Settings - Temporariamente oculto */}
              <Card className="glass-card border-border/50 hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-primary" />
                    Notificações
                  </CardTitle>
                  <CardDescription>Configure suas preferências de notificação</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Novos episódios</Label>
                      <p className="text-sm text-muted-foreground">
                        Receba avisos quando novos conteúdos forem lançados
                      </p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Lembretes de progresso</Label>
                      <p className="text-sm text-muted-foreground">
                        Receba lembretes para continuar sua jornada
                      </p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Atualizações por e-mail</Label>
                      <p className="text-sm text-muted-foreground">
                        Receba newsletters e novidades
                      </p>
                    </div>
                    <Switch />
                  </div>
                </CardContent>
              </Card>

              {/* Logout */}
              <Card className="glass-card border-border/50">
                <CardContent className="pt-6">
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sair da conta
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  )
}

export default UserProfile
